import { CSSProperties, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SetterOrUpdater, useRecoilValue, useSetRecoilState } from "recoil";

import { PIC } from "@ryde/assets";
import {
  FramerTooltip,
  RidePassengerTooltip,
  SelectOptionsDropdown,
} from "@ryde/controls";
import {
  bookingCalendarDataSelector,
  bookingCalendarRowIndexPassengerSF,
  buildFieldConfig,
  canResolveIfExists,
  FieldKey,
  IBookingCalendarPassengerDto,
  IExtendableDto,
  IPassengerInfo,
  Log,
  onActivateKeyDown,
  passengersListBookingAtom,
  selectedCellsBookingCalendarAtom,
  SelectionEnum,
  setCheckedHandler,
  updateListEntity,
  useDrawers,
  useSystemParams,
  wholeRowSelectedBookingCalendarSF,
} from "@ryde/shared";
import { blueMui } from "@ryde/theme";

import { ResolvedPassengerName } from "./resolved-passenger-name";

interface IBookingCalendarColumnPassengersCell {
  columnIndex: number;
  rowIndex: number;
  style: CSSProperties;
}

/** Same card as the ride passenger tooltip: the full data is loaded from the API on hover. */
const PassengerInfoTooltip = ({
  passenger,
  children,
}: {
  passenger: IBookingCalendarPassengerDto | null | undefined;
  children: ReactElement;
}): ReactElement => {
  const tooltipPassenger = useMemo<IPassengerInfo | undefined>(
    () =>
      passenger
        ? {
            id: passenger.passengerId,
            firstName: passenger.passengerName,
            code: passenger.code,
            unitId: passenger.unitId,
            unitName: passenger.unitName,
          }
        : undefined,
    [passenger],
  );

  if (!tooltipPassenger) {
    return children;
  }

  return (
    <RidePassengerTooltip
      key={tooltipPassenger.id}
      passenger={tooltipPassenger}
    >
      {children}
    </RidePassengerTooltip>
  );
};

export const BookingCalendarColumnPassengersCell = ({
  rowIndex,
  style,
}: IBookingCalendarColumnPassengersCell): ReactElement => {
  const { t } = useTranslation();
  const passenger = useRecoilValue(
    bookingCalendarRowIndexPassengerSF(rowIndex),
  );
  const setSelectedCell = useSetRecoilState(selectedCellsBookingCalendarAtom);
  const rowSelectedState = useRecoilValue(
    wholeRowSelectedBookingCalendarSF(passenger?.passengerId ?? 0),
  );
  const { rows } = useRecoilValue(bookingCalendarDataSelector);
  const setPassengersListBookingAtom = useSetRecoilState(
    passengersListBookingAtom,
  );
  const { openEditFormDrawer } = useDrawers();
  const { passengerCellData } = useSystemParams();

  const { fieldConfig, orderedFieldKeys } = buildFieldConfig(
    passengerCellData,
    t,
  );

  const activeNotActiveInfo = fieldConfig["active"];

  const onPassengerEdit = async (): Promise<void> => {
    if (!passenger?.passengerId) {
      Log.error(
        t("errors.noPassengerIdInBooking", {
          passengerId: passenger?.passengerId,
        }),
      );
      return;
    }

    const simpleResult = await openEditFormDrawer({
      entity: "passenger",
      id: passenger.passengerId,
      canResolve: canResolveIfExists,
    });

    if (!simpleResult.success) {
      Log.warn(
        t("errors.passengerDidntUpdate", {
          passengerId: passenger?.passengerId,
        }),
      );
      return;
    }

    updateListEntity(
      setPassengersListBookingAtom as unknown as SetterOrUpdater<
        IExtendableDto[]
      >,
      simpleResult.data as IExtendableDto,
    );
  };

  const setChecked = (value: SelectionEnum): void => {
    setCheckedHandler[value]({
      setter: setSelectedCell,
      rowOrCol: rows.get(passenger?.passengerId ?? 0) ?? new Set(),
    });
  };

  return (
    <div className="cell_pass_fixed" data-e2e="cell_pass" style={style}>
      <span className="checkbox">
        <SelectOptionsDropdown
          checked={rowSelectedState}
          setChecked={setChecked}
        />
      </span>
      <PassengerInfoTooltip passenger={passenger}>
        <div className="passenger_info">
          <div className="passenger_info_first_row">
            {activeNotActiveInfo?.show && (
              <FramerTooltip content={activeNotActiveInfo.getValue(passenger)}>
                {passenger?.isActive ? (
                  <PIC.UserActiveCircleSVG />
                ) : (
                  <PIC.UserInactiveCircleSVG />
                )}
              </FramerTooltip>
            )}

            <div
              className={"cell_simple pointer"}
              onClick={onPassengerEdit}
              onKeyDown={onActivateKeyDown(onPassengerEdit)}
              role="button"
              tabIndex={0}
              color={blueMui.A400}
            >
              <ResolvedPassengerName passenger={passenger} />
            </div>
          </div>

          {orderedFieldKeys.map(key => {
            const config = fieldConfig[key as FieldKey];
            if (!config?.show || config.excludeFromMapList) {
              return null;
            }
            const value = config.getValue(passenger);
            if (!value) {
              return null;
            }
            return (
              <span key={key} className={config.className}>
                {value}
              </span>
            );
          })}
        </div>
      </PassengerInfoTooltip>
    </div>
  );
};
